import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Brain,
  Target,
  Question,
  CaretDown,
  Gear,
  Lightning,
  ChartBar,
  Table,
  Users,
  Crosshair,
  ListChecks,
  Eye,
  Sparkle
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const OQueEoGeni = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'O Que é o GENI? Journal de Trading Automatizado',
      description: 'Descubra o que é o GENI e como ele ajuda traders a melhorar performance com análise de dados.',
      path: '/pt/o-que-e-o-geni',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/o-que-e-o-geni'),
    });

    const existingScript = document.querySelector('script[data-schema="faq-geni"]');
    if (existingScript) existingScript.remove();
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'faq-geni');
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "O que é o GENI?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "GENI é uma plataforma de análise de trading que automatiza o registro de operações e fornece métricas de performance para traders que querem evoluir com base em dados concretos."
          }
        },
        {
          "@type": "Question",
          "name": "GENI é um journal de trades?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sim, GENI funciona como um journal de trades automatizado. A diferença é que ele captura dados automaticamente e calcula métricas sem você precisar preencher planilhas manualmente."
          }
        },
        {
          "@type": "Question",
          "name": "GENI funciona para cripto?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sim, GENI foi desenvolvido com foco especial em traders de criptomoedas, com funcionalidades adaptadas para o mercado cripto."
          }
        },
        {
          "@type": "Question",
          "name": "Como o GENI funciona?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Você registra seus trades enviando screenshots ou preenchendo dados básicos. O GENI extrai informações automaticamente e calcula métricas como win rate, profit factor e RR médio."
          }
        }
      ]
    });
    document.head.appendChild(script);
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
      const schemaScript = document.querySelector('script[data-schema="faq-geni"]');
      if (schemaScript) schemaScript.remove();
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "O que é o GENI?",
      answer: "GENI é uma plataforma de análise de trading que automatiza o registro de operações e fornece métricas de performance para traders que querem evoluir com base em dados concretos. Diferente de planilhas tradicionais, o GENI elimina o trabalho manual e entrega análises prontas."
    },
    {
      question: "GENI é um journal de trades?",
      answer: "Sim, GENI funciona como um journal de trades automatizado. A diferença é que ele captura dados automaticamente e calcula métricas sem você precisar preencher planilhas manualmente. Isso elimina o atrito que faz a maioria dos traders abandonar o hábito de registrar operações."
    },
    {
      question: "GENI funciona para cripto?",
      answer: "Sim, GENI foi desenvolvido com foco especial em traders de criptomoedas. As funcionalidades foram pensadas para quem opera BTC, ETH, altcoins e memecoins, com suporte para múltiplos pares e timeframes."
    },
    {
      question: "Como o GENI funciona?",
      answer: "Você registra seus trades enviando screenshots dos gráficos ou preenchendo dados básicos. O GENI extrai informações automaticamente usando IA, organiza tudo em um histórico e calcula métricas como win rate, profit factor e RR médio em tempo real."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Navigation - Same as Landing.js */}
      <nav className="w-full px-6 py-5 flex items-center justify-between max-w-[1200px] mx-auto">
        <Link to="/" className="flex items-center flex-shrink-0">
          <img 
            src={LOGO_HORIZONTAL} 
            alt="GENI"
            className="h-10 md:h-11 lg:h-12 w-auto object-contain"
          />
        </Link>
        <div className="flex items-center gap-4">
          <Link 
            to="/login" 
            className="text-white/60 hover:text-white text-sm font-medium transition-colors"
          >
            Login
          </Link>
          <Link 
            to="/signup" 
            className="px-4 py-2 bg-white text-black text-sm font-semibold rounded-sm hover:bg-white/90 transition-colors"
          >
            Começar
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <Brain size={16} weight="fill" />
            <span>Sobre o GENI</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            O Que é o GENI?
          </h1>
          <p className="text-lg text-white/60">
            Explicação Completa
          </p>
        </header>

        <div className="bg-gradient-to-r from-[#00E676]/10 to-transparent border-l-2 border-[#00E676] p-6 rounded-r-lg mb-12">
          <p className="text-white/80 leading-relaxed">
            <strong className="text-white">GENI</strong> é um journal de trading automatizado que ajuda traders a analisar sua performance e tomar decisões melhores com base em dados reais. Em vez de confiar na memória ou em impressões subjetivas, você passa a operar com números concretos sobre o que funciona e o que não funciona no seu trading.
          </p>
        </div>

        {/* Seção 1: O Que o GENI Faz */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Gear size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">O Que o GENI Faz</h2>
          </div>
          
          <p className="text-white/70 leading-relaxed mb-6">
            Na essência, GENI resolve um problema que todo trader enfrenta: a falta de dados organizados sobre as próprias operações. Sem dados, você opera no escuro.
          </p>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center mb-4">
                <ListChecks size={20} className="text-[#00E676]" />
              </div>
              <h3 className="font-semibold text-white mb-2">Rastreia Trades</h3>
              <p className="text-white/50 text-sm">Cada operação é registrada com entrada, saída, tamanho, resultado e contexto.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center mb-4">
                <Table size={20} className="text-[#00E676]" />
              </div>
              <h3 className="font-semibold text-white mb-2">Organiza Dados</h3>
              <p className="text-white/50 text-sm">Histórico completo organizado por data, ativo, setup e resultado.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center mb-4">
                <ChartBar size={20} className="text-[#00E676]" />
              </div>
              <h3 className="font-semibold text-white mb-2">Analisa Performance</h3>
              <p className="text-white/50 text-sm">Métricas calculadas automaticamente: win rate, profit factor, RR médio.</p>
            </div>
          </div>
        </section>

        {/* Seção 2: Por Que Traders Precisam Disso */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Warning size={20} className="text-amber-400" />
            </div>
            <h2 className="text-2xl font-bold">Por Que Traders Precisam Disso</h2>
          </div>
          
          <p className="text-white/70 leading-relaxed mb-6">
            A maioria dos traders opera sem feedback estruturado. Isso cria problemas sérios que impedem a evolução.
          </p>

          <div className="space-y-4">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <h3 className="font-semibold text-amber-400 mb-2">Falta de Análise Real</h3>
              <p className="text-white/60 text-sm">Sem dados, você não sabe quais setups funcionam, em quais horários você performa melhor, ou quais ativos dão mais resultado. Opera no achismo.</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <h3 className="font-semibold text-amber-400 mb-2">Decisões Emocionais</h3>
              <p className="text-white/60 text-sm">Quando não há dados objetivos, as decisões são guiadas por memória e emoção. Você lembra dos acertos e esquece dos erros. O viés te engana.</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <h3 className="font-semibold text-amber-400 mb-2">Falta de Consistência</h3>
              <p className="text-white/60 text-sm">Sem um sistema de feedback, você repete erros sem perceber. A melhoria não acontece porque não há medição. O que não é medido não é gerenciado.</p>
            </div>
          </div>
        </section>

        {/* Seção 3: Principais Funcionalidades */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Lightning size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Principais Funcionalidades</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle size={18} className="text-[#00E676]" />
                <h3 className="font-semibold text-white">Journal Automático</h3>
              </div>
              <p className="text-white/50 text-sm">Envie um screenshot do gráfico e o GENI extrai os dados automaticamente. Sem preenchimento manual.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle size={18} className="text-[#00E676]" />
                <h3 className="font-semibold text-white">Analytics Completo</h3>
              </div>
              <p className="text-white/50 text-sm">Dashboard com todas as métricas que importam: win rate, profit factor, drawdown, RR médio realizado.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle size={18} className="text-[#00E676]" />
                <h3 className="font-semibold text-white">Comparação de Setups</h3>
              </div>
              <p className="text-white/50 text-sm">Veja qual setup realmente funciona. Compare performance entre diferentes estratégias lado a lado.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle size={18} className="text-[#00E676]" />
                <h3 className="font-semibold text-white">Insights Acionáveis</h3>
              </div>
              <p className="text-white/50 text-sm">O GENI identifica padrões no seu trading e mostra onde você está perdendo edge.</p>
            </div>
          </div>
        </section>

        {/* Seção 4: GENI vs Planilhas */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Table size={20} className="text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold">GENI vs Planilhas</h2>
          </div>
          
          <p className="text-white/70 leading-relaxed mb-6">
            Muitos traders começam com Excel ou Google Sheets. Funciona por um tempo, mas a realidade é dura.
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-white/60 font-medium"></th>
                  <th className="text-center py-3 px-4 text-white/60 font-medium">Planilha</th>
                  <th className="text-center py-3 px-4 text-[#00E676] font-medium">GENI</th>
                </tr>
              </thead>
              <tbody className="text-white/70">
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 font-medium">Registro</td>
                  <td className="py-3 px-4 text-center">Manual, demorado</td>
                  <td className="py-3 px-4 text-center text-[#00E676]">Automático</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 font-medium">Tempo gasto</td>
                  <td className="py-3 px-4 text-center">15-30 min/dia</td>
                  <td className="py-3 px-4 text-center text-[#00E676]">2-5 min/dia</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 font-medium">Análises</td>
                  <td className="py-3 px-4 text-center">Fórmulas manuais</td>
                  <td className="py-3 px-4 text-center text-[#00E676]">Automáticas</td>
                </tr>
                <tr className="border-b border-white/5">
                  <td className="py-3 px-4 font-medium">Taxa de abandono</td>
                  <td className="py-3 px-4 text-center">80%+</td>
                  <td className="py-3 px-4 text-center text-[#00E676]">Baixa</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium">Erros de dados</td>
                  <td className="py-3 px-4 text-center">Frequentes</td>
                  <td className="py-3 px-4 text-center text-[#00E676]">Raros</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-white/60 text-sm">
            O problema das planilhas não é a ferramenta — é o atrito. Cada campo que você precisa preencher é uma barreira. O GENI elimina esse atrito.
          </p>
        </section>

        {/* Seção 5: Para Quem é */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Users size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Para Quem é o GENI</h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
              <div className="w-12 h-12 rounded-full bg-[#00E676]/10 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">₿</span>
              </div>
              <h3 className="font-semibold text-white mb-2">Traders de Cripto</h3>
              <p className="text-white/50 text-sm">Desenvolvido com foco no mercado cripto e suas particularidades.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
              <div className="w-12 h-12 rounded-full bg-[#00E676]/10 flex items-center justify-center mx-auto mb-3">
                <ChartLineUp size={24} className="text-[#00E676]" />
              </div>
              <h3 className="font-semibold text-white mb-2">Day Traders</h3>
              <p className="text-white/50 text-sm">Registro rápido para quem faz múltiplas operações por dia.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
              <div className="w-12 h-12 rounded-full bg-[#00E676]/10 flex items-center justify-center mx-auto mb-3">
                <TrendUp size={24} className="text-[#00E676]" />
              </div>
              <h3 className="font-semibold text-white mb-2">Quem Quer Evoluir</h3>
              <p className="text-white/50 text-sm">Para quem leva trading a sério e quer melhorar com consistência.</p>
            </div>
          </div>
        </section>

        {/* Seção 6: Como Ajuda a Melhorar */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Target size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Como o GENI Ajuda Você a Melhorar</h2>
          </div>

          <div className="space-y-4">
            <div className="bg-[#00E676]/5 border border-[#00E676]/20 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <Eye size={24} className="text-[#00E676] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-2">Identifica Padrões</h3>
                  <p className="text-white/60 text-sm">Com dados suficientes, o GENI revela padrões que você não percebe: em quais condições você ganha mais, onde você perde edge, quais erros se repetem.</p>
                </div>
              </div>
            </div>
            <div className="bg-[#00E676]/5 border border-[#00E676]/20 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <Crosshair size={24} className="text-[#00E676] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-2">Melhora Consistência</h3>
                  <p className="text-white/60 text-sm">Quando você vê seus números, fica mais fácil seguir regras. O feedback constante cria um ciclo de melhoria contínua.</p>
                </div>
              </div>
            </div>
            <div className="bg-[#00E676]/5 border border-[#00E676]/20 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <Sparkle size={24} className="text-[#00E676] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-white mb-2">Elimina Erros Recorrentes</h3>
                  <p className="text-white/60 text-sm">Dados mostram claramente onde você está errando. Overtrading, saídas antecipadas, setups fracos — tudo fica visível nos números.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Seção 7: Resumo */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Brain size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Resumo</h2>
          </div>

          <div className="bg-gradient-to-r from-[#00E676]/10 to-transparent border-l-2 border-[#00E676] p-6 rounded-r-lg">
            <p className="text-white/80 leading-relaxed">
              <strong className="text-white">GENI não é apenas um journal</strong> — é um sistema de melhoria contínua no trading. Ele automatiza a parte chata (registro e cálculos) para que você possa focar no que importa: entender seus dados e tomar decisões melhores. Trading é um jogo de consistência, e consistência vem de feedback estruturado.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Question size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Perguntas Frequentes</h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-white/10 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  <CaretDown 
                    size={20} 
                    className={`text-white/60 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-white/70 leading-relaxed border-t border-white/5 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#00E676]/10 via-transparent to-transparent border border-[#00E676]/20 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Comece a Analisar Seus Trades</h2>
            <p className="text-white/70 mb-6 leading-relaxed max-w-xl mx-auto">
              Pare de operar no escuro. Use dados reais para entender seu trading e melhorar de verdade.
            </p>
            <Link 
              to="/signup"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#00E676] text-black font-semibold rounded-lg hover:bg-[#00E676]/90 transition-colors"
            >
              Começar Grátis
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </section>

        {/* Internal Links */}
        <section>
          <h2 className="text-xl font-bold mb-4">Artigos Relacionados</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link 
              to="/pt/diario-de-trades-automatico"
              className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#00E676]/50 transition-colors group"
            >
              <h3 className="font-medium text-white mb-1 group-hover:text-[#00E676] transition-colors">
                Diário de Trades Automático
              </h3>
              <p className="text-sm text-white/50">Como funciona e por que usar</p>
            </Link>
            <Link 
              to="/pt/como-analisar-trades"
              className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#00E676]/50 transition-colors group"
            >
              <h3 className="font-medium text-white mb-1 group-hover:text-[#00E676] transition-colors">
                Como Analisar Trades
              </h3>
              <p className="text-sm text-white/50">Técnicas práticas de análise</p>
            </Link>
            <Link 
              to="/pt/como-melhorar-no-day-trade"
              className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#00E676]/50 transition-colors group"
            >
              <h3 className="font-medium text-white mb-1 group-hover:text-[#00E676] transition-colors">
                Como Melhorar no Day Trade
              </h3>
              <p className="text-sm text-white/50">Evolução baseada em dados</p>
            </Link>
          </div>
        </section>

      </article>

      <footer className="border-t border-white/10 py-12">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src={LOGO_HORIZONTAL}
                alt="GENI" 
                className="h-8 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-white/30">© 2025 GENI</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OQueEoGeni;
